/*----------------------------------
- COMPONENT: PUBLIC DOMAINS TABLE
----------------------------------*/

/**
 * @docs docs/features/product-domain-listings
 * @adr ADR-0028
 * @fix docs/fixes/2026-11-05-domains-phone-table-reserve-before-mount.md
 * @rule No sort affordance, no pagination: the rows are the engine's daily order, bounded by the caller. The
 *       Sort control in the title block opens authentication instead of reordering, and a query-string sort
 *       would turn the page `noindex`.
 *
 * The public listing's table on the design system (batch 7 part 3, docs/mockups/website-domains.html): the
 * system's table (`ud-table`, sentence-case heads, the zebra, the name and the "+" pinned) in the marketing table
 * card (`wp-tcard`, the last two rows fading where the sample ends, `wp-fade`), the foot holding the est. profit
 * legend and See all with its count.
 *
 * On the kit `Table` since batch 6 S2b: its column tracks (the mock's `colgroup`), the row test id, the cell
 * `data-column` hooks, the row header's body look and the teaser rows (`inert`) are the kit's (S2a). Its phone form
 * stays this page's own cards (`PublicDomainsRowCards`), so the kit's phone rows are off (`phone={false}`).
 *
 * A real `<table>`, not a grid with roles: search engines and the retrievers that turn pages into Markdown extract
 * `<table>` elements, and this table is the page's evidence.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { Button } from '@uniquedomains/components/kit/button/Button';
import { Glyph } from '@uniquedomains/components/kit/glyph/Glyph';
import { IconButton } from '@uniquedomains/components/kit/icon-button/IconButton';
import { Name } from '@uniquedomains/components/kit/name/Name';
import { Rating } from '@uniquedomains/components/kit/rating/Rating';
import { Cell } from '@uniquedomains/components/kit/table/Cell';
import { Table } from '@uniquedomains/components/kit/table/Table';
import type { TableColumn, TableRow } from '@uniquedomains/components/kit/table/tableTypes';
import { cx } from '@uniquedomains/components/kit/utils/cx';
import { HoverCard } from '@uniquedomains/components/marketing/hover-card/HoverCard';
import { TableFoot } from '@uniquedomains/components/marketing/table-card/TableFoot';
import { BROWSE_ESTIMATE_SPOKEN_LABEL } from '@uniquedomains/components/radar/catalogs/browseResultCountCopy';
import Icon from '@/client/components/ui/icon/Icon';
import { pickCardSeed } from '@/client/catalogs/landing/domains/cardVisuals';
import { PUBLIC_PREVIEW_TABLE_COPY, fillPreviewCopy } from '@/client/catalogs/landing/domains/publicPreviewCopy';

import PublicDomainsTableCell from './PublicDomainsTableCell';
import PublicTableHoverCard from './PublicTableHoverCard';
import { resolvePublicCellValue, resolvePublicRating } from './publicCellValues';
import { splitDomainLabel } from './publicTableIdentity';

// Types
import type { TableCellRow } from '@uniquedomains/components/domains/table/cellValues';
import type { DomainsSearchPublicTableRow } from '@uniquedomains/components/contracts/domains/search';
import type { DatabaseBrowseCountPrecision } from '@uniquedomains/components/contracts/domains/browseFacets';
import type { PublicDomainsTableColumn } from '@/client/catalogs/landing/domains/publicTableColumns';

import './domainsTable.css';

/*----------------------------------
- TYPES
----------------------------------*/

export type PublicDomainsTableRow = {
    key: string;
    /**
     * The sanitized public row. Typed as the resolver's structural row rather than the private
     * `DomainsSearchRow`, so the public shape satisfies it without growing fields it never
     * publishes.
     */
    row: TableCellRow;
    projection: DomainsSearchPublicTableRow | null;
};

type PublicDomainsTableProps = {
    rows: readonly PublicDomainsTableRow[];
    columns: readonly PublicDomainsTableColumn[];
    /** Per-column count of rows on this page carrying a reading. */
    fillCounts: Record<string, number>;
    totalCountLabel: string | null;
    /** Adds the spoken "(estimated)" after a sampled total (ADR-0034 section 5). */
    countPrecision: DatabaseBrowseCountPrecision;
    /**
     * False inside a frame that is already a surface (Home's demo window): the table sits flush, without the
     * marketing card's border and shadow.
     */
    framed: boolean;
    /**
     * True for the server render and the hydration frame, before the block knows the viewport. The table then carries
     * `dm-tcard--ssr`, which hides it below 1024px (domainsTable.css), so a tablet or a phone never paints the desktop
     * table before the mount swaps in the phone cards (the band review, batch 6 S6).
     */
    beforeMount: boolean;
    /** Opening a row names it, so the drawer and the wall behind it answer with that domain rather than the list. */
    onOpenRow: (domain: string) => void;
    /** The row "+", naming the row too: its wall is the Watchlist gate, not See all. */
    onAddRow: (domain: string) => void;
    onSeeAll: () => void;
};

/*----------------------------------
- HELPERS
----------------------------------*/

/** One column's head: its label, and the provenance card (What, From, Refreshed, On this page). */
const headContent = (column: PublicDomainsTableColumn, filled: number, total: number) => {
    const copy = PUBLIC_PREVIEW_TABLE_COPY.head;
    const lines = [
        [copy.what, column.provenance.what],
        [copy.from, column.provenance.from],
        [copy.refreshed, column.provenance.refreshed],
        [copy.onPage, fillPreviewCopy(copy.filled, { filled, total })],
    ];
    return (
        <HoverCard
            trigger={column.label}
            label={column.label}
            // The journey focuses the head's trigger and reads this card (conversion-trigger-journey, journey 2).
            testId={`domains-column-card-${column.id}`}
            body={
                <dl class="dm-hcdl">
                    {lines.map(([term, detail]) => [<dt key={`${term}-t`}>{term}</dt>, <dd key={`${term}-d`}>{detail}</dd>])}
                </dl>
            }
        />
    );
};

/** One value cell's content with its evidence: the resolver's card, else the one-line reason the cell gives. */
const valueContent = (column: PublicDomainsTableColumn, entry: PublicDomainsTableRow) => {
    const { row, projection } = entry;
    if (column.id === 'verdict') {
        const rating = resolvePublicRating(projection);
        return rating ? (
            <PublicTableHoverCard
                // The kit writes the band and confidence as a `title`; the hover card says it, so no native tooltip doubles it.
                trigger={<Rating band={rating.band} confidence={rating.confidence} title={undefined} />}
                line={{ label: column.label, text: rating.hover }}
            />
        ) : <span class="dm-none">–</span>;
    }
    const value = resolvePublicCellValue(column.id, row, projection);
    // The status chip's own title is its label: a card that repeats the chip says nothing (the mock draws none).
    const line = value.kind !== 'chip' && value.title ? { label: column.label, text: value.title } : null;
    return <PublicTableHoverCard trigger={<PublicDomainsTableCell value={value} status={row.status} />} hover={value.hover} line={line} />;
};

/** The rows `wp-fade` blurs where the sample ends (marketing.css, `tr:nth-last-child(-n+2)`). */
const FADED_ROW_COUNT = 2;

/** The name and the "+" columns' keys (the catalog's columns sit between them). */
const NAME_KEY = 'name';
const ACT_KEY = 'act';

/**
 * The catalog's tracks plus the 12px gutters (the mock's `colgroup`), by column key: the table lays out fixed at their
 * sum (1756px), so every list keeps the same columns, a long cell ends in an ellipsis rather than pushing its
 * neighbours, and the row scrolls inside its wrap with the name and the "+" pinned. The rating is wider than the
 * shipped 72px: it carries the system's rating, a label and three dots, not five stars.
 */
const TRACKS: Readonly<Record<string, string>> = {
    [NAME_KEY]: '248px',
    acquisition: '100px',
    fingerprint: '172px',
    timing: '124px',
    signals: '92px',
    verdict: '136px',
    askPrice: '88px',
    upside: '132px',
    similarSales: '248px',
    comps: '144px',
    liquidity: '112px',
    renewal: '96px',
    [ACT_KEY]: '64px',
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PublicDomainsTable = ({
    rows,
    columns,
    fillCounts,
    totalCountLabel,
    countPrecision,
    framed,
    beforeMount,
    onOpenRow,
    onAddRow,
    onSeeAll,
}: PublicDomainsTableProps) => {
    const copy = PUBLIC_PREVIEW_TABLE_COPY;
    const cols: TableColumn[] = [
        { key: NAME_KEY, label: copy.nameHead, rowHeader: true },
        ...columns.map((column): TableColumn => ({
            key: column.id,
            label: column.label,
            align: column.align === 'right' ? 'right' : undefined,
            head: headContent(column, fillCounts[column.id] ?? 0, rows.length),
            cellAttrs: { 'data-column': column.id },
        })),
        { key: ACT_KEY, label: copy.actionsHead, align: 'right', head: <span class="ud-sr-only">{copy.actionsHead}</span> },
    ];
    const tableRows: TableRow[] = rows.map((entry, index) => {
        const { domain } = entry.row;
        const label = splitDomainLabel(domain);
        const cells: TableRow['cells'] = {
            /* Named by its content, "Open summit.co": a hidden verb, then the visible name (the glyph is `aria-hidden`).
               No `aria-label`: the glyph's letter is visible text an `aria-label` would not hold (WCAG 2.5.3, axe's
               label-content-name-mismatch). Wrapped: handed straight to onClick, the handler would receive the event
               where it expects the name. */
            [NAME_KEY]: (
                <button type="button" class="dm-name" onClick={() => onOpenRow(domain)}>
                    <span class="ud-sr-only">{copy.openRowVerb} </span>
                    <Cell icon={<Glyph letter={label.name} seed={pickCardSeed(domain)} />}>
                        <Name word={label.name} tld={label.tld.replace(/^\./, '') || undefined} />
                    </Cell>
                </button>
            ),
            [ACT_KEY]: (
                <IconButton
                    label={fillPreviewCopy(copy.addRow, { domain })}
                    title={copy.addRowTitle}
                    icon={<Icon name="plus" size={14} strokeWidth={2.2} />}
                    onClick={() => onAddRow(domain)}
                />
            ),
        };
        for (const column of columns) cells[column.id] = valueContent(column, entry);
        return {
            id: entry.key,
            testId: 'domains-table-row',
            /* The faded rows are hidden from assistive tech and the keyboard too (Gaetan, 2026-10-28, review M2 answer
               A): the fade leaves their text at 3.5:1 and 1.7:1 and their focus ring invisible, so a reachable faded row
               was a name a sighted keyboard user opened without reading it. They stay in the HTML and in the Markdown
               mirror; the phone cards draw every row in clear. */
            inert: index >= rows.length - FADED_ROW_COUNT,
            cells,
        };
    });

    // Before the mount a phone hides this table but keeps its box, sized by the row count (`--dm-rows`, the sheet's
    // reserve per card): the sections below would otherwise sit in the viewport and drop by the cards' height when
    // they swap in (CLS 0.567 on a throttled phone, mock parity F2 item 4).
    return (
        <div class={cx(framed && 'ud-card ud-card--flush wp-tcard', 'dm-tcard', beforeMount && 'dm-tcard--ssr')} style={beforeMount ? { '--dm-rows': rows.length } : undefined}>
            {/* `wp-fade`: the last two rows blur where the bounded sample ends (marketing.css). */}
            <Table cols={cols} rows={tableRows} tracks={TRACKS} caption={copy.caption} phone={false} class="wp-fade dm-table" />
            <TableFoot>
                <div class="dm-foot">
                    <span class="dm-legend" aria-hidden="true">
                        <span><i class="dm-mk dm-mk--price" />{copy.legend.price}</span>
                        <span><i class="dm-mk dm-mk--median" />{copy.legend.median}</span>
                        <span><i class="dm-mk dm-mk--estimate" />{copy.legend.estimate}</span>
                    </span>
                    {/* The accessible name stays "See all ~N (estimated) domains": the journeys read it whole. */}
                    <Button variant="ink" size="md" cta data-testid="domains-see-all" onClick={onSeeAll}>
                        <span>
                            {copy.seeAllLead}
                            {totalCountLabel ? (
                                <>
                                    {' '}
                                    <b>{totalCountLabel}</b>
                                    {countPrecision === 'sampled' ? <span class="ud-sr-only"> {BROWSE_ESTIMATE_SPOKEN_LABEL}</span> : null}
                                </>
                            ) : null}{' '}
                            {copy.seeAllTail}
                        </span>
                    </Button>
                </div>
            </TableFoot>
        </div>
    );
};

export default PublicDomainsTable;
